import React, { useState } from "react";
import { useFela } from "react-fela";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";

import { Accordion } from "@/components/Accordion";
import { localesFilesMap } from "@/locales/map";
import { IColors } from "@/hooks/useTheme/types";
import { useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { Heading } from "@/components/Heading";
import { Image } from "@/components/Image";
import { IImage } from "@/typings/common";
import { LinkButton } from "@/components/LinkButton";

const customWrapperStyles = {
  marginBottom: 6,
  borderBottom: "none",
  nested: {
    ":first-child": {
      borderTop: "none",
    },
  },
};

const getCustomStyles = (isActive: boolean, colors: IColors) => ({
  padding: "18px 30px 18px 20px",
  borderRadius: 8,
  backgroundColor: isActive ? colors.GHOST_WHITE1 : "inherit",
  nested: {
    "> div": {
      padding: 0,
    },
    "> div > p": {
      fontSize: 24,
    },
  },
});

export const ConvertEverythingSection: React.FC<{ images: IImage[] }> = ({
  images,
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const { pathname } = useRouter();

  const translationPrefix =
    pathname === "/" ? "landingPage" : `page/${localesFilesMap[pathname]}`;

  return (
    <Grid
      id="ConvertEverythingSection"
      customStyle={{
        position: "relative",
        flexDirection: "column",
        [bp.FROM_DESKTOP]: {
          flexDirection: "row",
          justifyContent: "space-between",
          minHeight: 700,
        },
      }}
    >
      <div
        className={css({
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          [bp.FROM_DESKTOP]: { alignItems: "flex-start", marginRight: 120 },
        })}
      >
        <Heading as="h2">{t("section/convertEverything:title")}</Heading>

        <div className={css({ width: "100%", marginTop: 30 })}>
          <Accordion
            title={t("section/convertEverything:symbolsComponents")}
            longAnswer={t(
              `${translationPrefix}:section/convertEverything.symbolsComponents`,
            )}
            icon="RHOMB-ICON"
            toggle={() => setActiveTab(0)}
            active={activeTab === 0}
            openedIcon="TRIANGLE-UP"
            closedIcon="TRIANGLE-DOWN"
            customWrapper={customWrapperStyles}
            customStyle={getCustomStyles(activeTab === 0, colors)}
            customStyleContent={{ paddingTop: 27 }}
          />
          <Accordion
            title={t("section/convertEverything:prototypingTransitions")}
            longAnswer={t(
              `${translationPrefix}:section/convertEverything.prototypingTransitions`,
            )}
            icon="ARROW-POINTER-ICON"
            toggle={() => setActiveTab(1)}
            active={activeTab === 1}
            openedIcon="TRIANGLE-UP"
            closedIcon="TRIANGLE-DOWN"
            customWrapper={customWrapperStyles}
            customStyle={getCustomStyles(activeTab === 1, colors)}
            customStyleContent={{ paddingTop: 27 }}
          />

          <Accordion
            title={t("section/convertEverything:textAndColorStyles")}
            longAnswer={t(
              `${translationPrefix}:section/convertEverything.textAndColorStyles`,
            )}
            icon="FILL-ICON"
            toggle={() => setActiveTab(2)}
            active={activeTab === 2}
            openedIcon="TRIANGLE-UP"
            closedIcon="TRIANGLE-DOWN"
            customWrapper={customWrapperStyles}
            customStyle={getCustomStyles(activeTab === 2, colors)}
            customStyleContent={{ paddingTop: 27 }}
          />
        </div>
        <LinkButton
          title={t("section/convertEverything:buttonText")}
          href="/converter-features"
          customStyle={{
            width: "max-content",
            color: colors.ORANGE,
            backgroundColor: "none",
            border: "none",
            nested: {
              "> svg": {
                marginLeft: 23,
                nested: {
                  "> path": {
                    stroke: colors.ORANGE,
                  },
                },
              },
            },
          }}
          icon="ARROW-RIGHT"
        />
      </div>
      <div
        className={css({
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        <div
          className={css({
            minHeight: 500,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 20,
            background:
              "url('/svgs/background-bubble-big.svg') no-repeat center",
            backgroundSize: "contain",
            [bp.ONLY_TABLET]: {
              width: "60%",
            },
            [bp.FROM_DESKTOP]: {
              margin: "0 auto",
            },
          })}
        >
          <Image
            customStyle={{
              maxWidth: "100%",
              margin: "0 auto",
              borderRadius: 8,
            }}
            src={images[activeTab].src}
            alt={images[activeTab].alt}
          />
        </div>
      </div>
    </Grid>
  );
};
